import { useEffect, useState, useRef } from 'react'
import { api } from '../services/api'
import { useAuth } from './use-auth'
import type { UserRole, FleetNavSummary, NavBadge } from '@alpha-edge/shared'

const POLL_INTERVAL_MS = 30_000

const EMPTY_BADGE: NavBadge = { count: 0, severity: 'info' }
const EMPTY_SUMMARY: FleetNavSummary = { alerts: EMPTY_BADGE, devices: EMPTY_BADGE }

const ALERT_ROLES: readonly UserRole[] = ['customer_admin', 'medflow_admin']

export interface AlertSummary {
  readonly activeCount: number
  readonly criticalCount: number
  readonly warningCount: number
  readonly nav: FleetNavSummary
}

const EMPTY: AlertSummary = {
  activeCount: 0,
  criticalCount: 0,
  warningCount: 0,
  nav: EMPTY_SUMMARY,
}

function worstSeverity(...badges: readonly NavBadge[]): 'critical' | 'warning' | 'info' {
  if (badges.some((b) => b.severity === 'critical')) return 'critical'
  if (badges.some((b) => b.severity === 'warning')) return 'warning'
  return 'info'
}

export function rollupBadge(...badges: readonly NavBadge[]): NavBadge {
  const count = badges.reduce((sum, b) => sum + b.count, 0)
  return { count, severity: count > 0 ? worstSeverity(...badges) : 'info' }
}

export function useAlertSummary(): AlertSummary {
  const { user } = useAuth()
  const role = user?.role as UserRole | undefined
  const enabled = role !== undefined && ALERT_ROLES.includes(role)
  const [summary, setSummary] = useState<AlertSummary>(EMPTY)
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null)

  useEffect(() => {
    if (!enabled) return

    let cancelled = false

    function fetchSummary() {
      api
        .get<FleetNavSummary>('/fleet/alerts/nav-summary')
        .then((res) => {
          if (cancelled) return
          const nav = res.data ?? EMPTY_SUMMARY
          const rolled = rollupBadge(nav.alerts, nav.devices)
          setSummary({
            activeCount: rolled.count,
            criticalCount: nav.alerts.severity === 'critical' ? nav.alerts.count : 0,
            warningCount: rolled.count - (nav.alerts.severity === 'critical' ? nav.alerts.count : 0),
            nav,
          })
        })
        .catch(() => {
          if (!cancelled) setSummary(EMPTY)
        })
    }

    fetchSummary()
    timerRef.current = setInterval(fetchSummary, POLL_INTERVAL_MS)

    return () => {
      cancelled = true
      if (timerRef.current) clearInterval(timerRef.current)
    }
  }, [enabled])

  return summary
}
